// context/SuperAdminContext.jsx
// ADDED:
//   isTablet                      — boolean, persisted in localStorage
//   fetchAdminData()              — GET  baseurl/ondemand-worker-reports/admin-data
//   fetchOnboardingOverallReport(payload) — POST baseurl/ondemand-worker-reports/onboarding-overall-report

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SuperAdminContext = createContext(undefined);

export const SuperAdminProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Location management states
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  }), [token]);

  const persistToken = (newToken) => {
    localStorage.setItem("superadmintoken", newToken);
    setToken(newToken);
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem("superadmintoken");
    localStorage.removeItem("isUTV");
    localStorage.removeItem("isTablet");
    setToken(null);
    setUser(null);
    setIsTablet(false);
  }, []);

  const verifyToken = useCallback(async (tokenToVerify) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` },
      });

      if (response.data?.error?.name === "TokenExpiredError") {
        clearSession();
        return false;
      }

      if (response.data?.success && response.data?.user) {
        const u = response.data.user;
        setUser({
          id: u.id,
          phone: u.phone,
          name: u.name,
          role: u.role,
          isSuperAdmin: u.role === "admin",
          UTVMember: u.UTVMember ?? false,
          featureAccess: u.featureAccess ?? [],
          MyCompany: u.MyCompany,
          CompanyLocation: u.CompanyLocation,
        });
      }

      return true;
    } catch (error) {
      console.error("Token verification failed:", error);
      clearSession();
      return false;
    }
  }, [clearSession]);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = localStorage.getItem("superadmintoken");
        const storedIsTablet = localStorage.getItem("isTablet");
        if (storedIsTablet !== null) setIsTablet(storedIsTablet === "true");
        if (storedToken) {
          setToken(storedToken);
          await verifyToken(storedToken);
        }
      } catch (error) {
        console.error("Session restore failed:", error);
        clearSession();
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };
    restoreSession();
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────

  const sendOtp = async (phone) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/login-super`,
        { phone },
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Send OTP failed:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (phone, otp) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/verify-super-otp`,
        { phone, otp },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data;
      if (data.token) {
        persistToken(data.token);
        document.cookie = `superAdminToken=${data.token}; path=/`;
        localStorage.setItem("isUTV", data.user?.UTVMember ?? false);

        const tabletFlag = data.isTablet ?? false;
        localStorage.setItem("isTablet", tabletFlag);          
        setIsTablet(tabletFlag);

        if (data.user) {
          setUser({
            id: data.user.id,
            phone: data.user.phone,
            name: null,
            role: data.user.role,
            isSuperAdmin: data.user.isSuperAdmin,
            UTVMember: data.user.UTVMember,
            featureAccess: [],
            MyCompany: data.user.MyCompany,
          });
        }
        await verifyToken(data.token);
      }
      return data;
    } catch (error) {
      console.error("Verify OTP failed:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => clearSession();

  // ── Data APIs ─────────────────────────────────────────────────────────────

  const searchWorker = useCallback(async (searchParams) => {
    const response = await axios.get(
      `${BASE_URL}/on-boarding/search-worker`,
      {
        params: searchParams,
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  }, [getAuthHeaders]);

  const fetchLocations = useCallback(async () => {
    const response = await axios.get(
      `${BASE_URL}/lenskart/locations?isActive=false`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  const fetchSkills = useCallback(async () => {
    const response = await axios.get(
      `${BASE_URL}/lenskart/skills?isActive=false`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

    const fetchaccessories = useCallback(async () => {
    const response = await axios.post(
      `${BASE_URL}/onDemandAccessories/list-accessories`,
      {},
      { headers: getAuthHeaders() }
    );
    return response;
  }, [getAuthHeaders]);

      const fetchaccessoriesbycompany = useCallback(async (companyId) => {
    const response = await axios.post(
      `${BASE_URL}/onDemandAccessories/get-accessory/company`,
      {companyId},
      { headers: getAuthHeaders() }
    );
    return response;
  }, [getAuthHeaders]);

    const fetchaccessorieshistory = useCallback(async (workerId) => {
    const response = await axios.post(
      `${BASE_URL}/onDemandAccessories/get-accessory-history/worker`,
      {workerId},
      { headers: getAuthHeaders() }
    );
    return response;
  }, [getAuthHeaders]);

  const fetchCompaniesByLocations = useCallback(async (locationIds = []) => {
    if (!locationIds.length) return { companies: [] };
    const response = await axios.post(
      `${BASE_URL}/onDemandCompanies/list-companies`,
      { locationIds },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  const fetchOnboardingWorkers = useCallback(async (filters = {}) => {
    const response = await axios.post(
  `${BASE_URL}/on-boarding/filters`,
  { ...filters, filterSource: 'onboarding' },
  { headers: getAuthHeaders() }
);
return response.data;
  }, [getAuthHeaders]);

  const downloadWorkersCSV = useCallback(async (filters = {}) => {
    try {
      const payload = { ...filters, downloadCsv: true , filterSource: 'onboarding' };
      const response = await axios.post(
        `${BASE_URL}/on-boarding/filters`,
        payload,
        {
          headers: getAuthHeaders(),
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to download workers CSV:", error);
      throw error;
    }
  }, [getAuthHeaders]);

  const fetchWorkerVerificationHistory = useCallback(async (workerId,lastCompanyName, {category}) => {
    const response = await axios.post(
      `${BASE_URL}/on-boarding/verification-history`,
      { workerId,
        lastCompanyName,
        category
       },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  const fetchLastCallStatuses = useCallback(async (activetab) => {
    const response = await axios.get(
      `${BASE_URL}/on-boarding/list-status/${activetab}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

    const fetchRetentionIssues = useCallback(async () => {
    const response = await axios.get(
      `${BASE_URL}/on-boarding/get-issues`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);


  const fetchLastCallSubStatuses = useCallback(async (activetab,status) => {

    const response = await axios.get(
      `${BASE_URL}/on-boarding/list-sub-status/${status}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  // const updateWorkerVerificationStatus = useCallback(async (workerId, status, remarks) => {
  //   const response = await axios.post(
  //     `${BASE_URL}/on-boarding/update-verification-status`,
  //     { workerId, status, remarks },
  //     { headers: getAuthHeaders() }
  //   );
  //   return response.data;
  // }, [getAuthHeaders]);


const updateWorkerVerificationStatus = useCallback(
  async (workerId, data) => {
    const response = await axios.post(
      `${BASE_URL}/on-boarding/update-verification-status`,
      {
        workerId,
        ...data,
      },
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  },
  [getAuthHeaders]
);

  const downloadWorkerStepsCSV = useCallback(async (fromDate, toDate) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/ondemand-worker-reports/daily-worker-steps-count/csv`,
        {
          params: { fromDate, toDate },
          headers: getAuthHeaders(),
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to download worker steps CSV:", error);
      throw error;
    }
  }, [getAuthHeaders]);

  // ── Registered Workers Count + CSV ───────────────────────────────────────

  const fetchRegisteredWorkersCount = useCallback(async (fromDate, toDate) => {
    const response = await axios.get(
      `${BASE_URL}/ondemand-worker-reports/registered-workers-count`,
      {
        params: { fromDate, toDate },
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  }, [getAuthHeaders]);


    const changepayoutmode = async (mode, appliedFromISO,workerId) => {
    try {
  
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-payout-frequency`,
        {
          workerId,
          payoutFrequency: mode.toLowerCase(),
          appliedFrom: appliedFromISO,
        },
        {
           headers: {
            Authorization: `Bearer ${token}`,
            
          }
        }
      );
  
      return response;
    } catch (error) {
      console.error("Failed to change payout mode:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const worker_payout_history = async (workerId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-frequency-history`,
        { workerId },
        {  headers: {
            Authorization: `Bearer ${token}`,
            
          }
        }
      );
  
      return response.data;
    } catch (error) {
      console.error("Failed to fetch payout history:", error);
      return { success: false, data: { history: [] } };
    } finally {
      setLoading(false);
    }
  };

  const downloadRegisteredWorkersCSV = useCallback(async (fromDate, toDate) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/ondemand-worker-reports/registered-workers-count/csv`,
        {
          params: { fromDate, toDate },
          headers: getAuthHeaders(),
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to download registered workers CSV:", error);
      throw error;
    }
  }, [getAuthHeaders]);

  // ── Overall Onboarding Report ─────────────────────────────────────────────

  const fetchOnboardingOverallReport = useCallback(async (payload = {}) => {
    const response = await axios.post(
      `${BASE_URL}/ondemand-worker-reports/onboarding-overall-report`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  // ── Admin Data ────────────────────────────────────────────────────────────

  const fetchAdminData = useCallback(async () => {
    const response = await axios.get(
      `${BASE_URL}/ondemand-worker-reports/admin-data`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  const fetchSources = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/lenskart-admin/list-source`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch sources:", error);
      throw error;
    }
  }, [getAuthHeaders]);

 const uploadCsvForSignups = useCallback(async (file)=> {
  try{
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${BASE_URL}/lenskart/upload-eligible-workers`,
      {headers: getAuthHeaders(),
        body: formData
      },
      
    );
    return response.data;
  } catch (error){
    console.error("Failed to upload CSV for signups:", error);
    throw error;
  }
 }, [getAuthHeaders]);

 const getCompanyByLocation = useCallback(async (locationId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lenskart/getCompany/Skills`,
      { locationId },          
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch skills:", error);
    throw error;
  }
}, [getAuthHeaders]);

const getDepartmentsByCompany = useCallback(async (companyId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/onDemandCompanies/list-departments`,
      { companyId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch departments by company:", error);
    throw error;
  }
}, [getAuthHeaders]);


//change payout date endpoints

const payout_change_date_shifts = useCallback(async (workerId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/shifts-by-user-Id`,
      {workerId},
      { headers: getAuthHeaders() }
    );
    return response;
  } catch (error) {
    console.error("Failed to fetch companies by location:", error);
    throw error;
  }
}, [getAuthHeaders]);


const change_payout_date = useCallback(async (workerId,shiftIds,newPaymentDate,reason) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/full-payout-withdrawal`,
      {
        workerId,
        shiftIds,
        newPaymentDate,
        reason
      },
      { headers: getAuthHeaders() }
    );
    return response;
  } catch (error) {
    console.error("Failed to fetch companies by location:", error);
    throw error;
  }
}, [getAuthHeaders]);


// Superadmin request history functions:

const fetchCompaniesByLocation = useCallback(async (locationId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/onDemandCompanies/list-companies/${locationId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch companies by location:", error);
    throw error;
  }
}, [getAuthHeaders]);


const fetchActiveCompaniesByLocation = useCallback(async (locationId,isActive) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/onDemandCompanies/list-companies/${locationId}`,

      { headers: getAuthHeaders(),
        params: { isActive },
       }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch companies by location:", error);
    throw error;
  }
}, [getAuthHeaders]);

const fetchDepartmentTimeSlots = useCallback(async (department, departmentId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lenskart/department-dropdown`,
      { department, departmentId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch department time slots:", error);
    throw error;
  }
}, [getAuthHeaders]);

const fetchFloorAdminHistory = useCallback(async ({ department, departmentIds, companyId, status }) => {
  try {
    const encodedDept = encodeURIComponent(department);
    const encodedDeptIds = encodeURIComponent(departmentIds);
    let url = `${BASE_URL}/floor-admin/department?department=${encodedDept}&departmentIds=${encodedDeptIds}`;
    if (status && status !== "all") url += `&status=${status}`;
    if (companyId) url += `&companyId=${companyId}`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch floor admin history:", error);
    throw error;
  }
}, [getAuthHeaders]);

const updateFloorRequestStatus = useCallback(async (requestIds, status, reason = "") => {
  try {
    const payload = { requestIds, status };
    if (status === "rejected" && reason) payload.reason = reason;
    const response = await axios.post(
      `${BASE_URL}/floor-admin/update-status`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update floor request status:", error);
    throw error;
  }
}, [getAuthHeaders]);

const addFloorRequirement = useCallback(async (requestData) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/floor-admin/add`,
      requestData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to add floor requirement:", error);
    throw error;
  }
}, [getAuthHeaders]);

const updateFloorRequirement = useCallback(async (payload) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/floor-admin/update`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update floor requirement:", error);
    throw error;
  }
}, [getAuthHeaders]);

const deleteFloorRequirement = useCallback(async ({ date, slot, department, company, location }) => {
  try {
    const response = await axios.delete(`${BASE_URL}/floor-admin/delete`, {
      headers: getAuthHeaders(),
      data: { date, slot, Department: department, company, location },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to delete floor requirement:", error);
    throw error;
  }
}, [getAuthHeaders]);

const attendancefiledatafetch = async (formdata,selectedCompany,token) => {
    try {
      setLoading(true);
      console.log("token:", token)
      const response = await axios.post(`${BASE_URL}/lenskart-admin/validate-bulk-mark-entry/${selectedCompany}`, 
        formdata,
        {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
     
      console.log('dsdss',response.data);
      //console.log(token);
      return response.data;
    } catch (error) {
  console.error(
    "Upload failed:",
    error.response?.data || error.message
  );
  throw error;
}
 finally {
      setLoading(false);
    }
  }

  const fetchMonthlyPayout = useCallback(async (workerId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/worker-monthly-referral-payout/${workerId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch monthly payout:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // ── Location Management Functions ──────────────────────────────────────

  /**
   * Fetch all locations
   * GET /lenskart/locations?isActive=false
   */
  const fetchAllLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
        headers: getAuthHeaders(),
        params: {
          isActive: false,
        },
      });
      return response;
    } catch (error) {
      console.error('Fetching locations failed:', error);
      throw error;
    }
  }, [getAuthHeaders]);

    const fetchActiveLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
        headers: getAuthHeaders(),
        params: {
          isActive: true,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Fetching locations failed:', error);
      throw error;
    }
  }, [getAuthHeaders]);



  // ── fetchalllocation (alias for fetchAllLocations) ────────────────────

  /**
   * Fetch all locations (alias)
   * GET /lenskart/locations
   */
  const fetchalllocation = useCallback(async () => {
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          isActive: false,
        },
      });
      return response;
    } catch (error) {
      console.error('Fetching job details failed:', error);
      throw error;
    }
  }, []);

  /**
   * Add a new location
   * POST /lenskart/add-location
   */
  const addLocation = useCallback(async (locationName) => {
    try {
      setAdding(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/add-location`,
        { name: locationName.trim() },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Adding location failed:', error);
      throw error;
    } finally {
      setAdding(false);
    }
  }, [getAuthHeaders]);

  /**
   * Toggle location active/inactive status
   * PUT /lenskart/mark-location-active-inactive/{locationId}
   */
  const locationStatusChange = useCallback(async (location) => {
    setTogglingId(location.id);
    try {
      const response = await axios.put(
        `${BASE_URL}/lenskart/mark-location-active-inactive/${location.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Location status change failed:', error);
      throw error;
    } finally {
      setTogglingId(null);
    }
  }, [getAuthHeaders]);

  const editLocation = useCallback(async (editValue, location) => {
    setSavingEdit(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/lenskart/edit-location/${location.id}`,
        { name: editValue.trim() },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Editing location failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);

  // ── Skill Management Functions ──────────────────────────────────────

  /**
   * Fetch all skills
   * GET /lenskart/skills?isActive=false
   */
  const fetchAllSkills = useCallback(async (status) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/skills`, {
        headers: getAuthHeaders(),
        params: {
          isActive: status,
        },
      });
      return response;
    } catch (error) {
      console.error('Fetching skills failed:', error);
      throw error;
    }
  }, [getAuthHeaders]);

  

  /**
   * Add a new skill
   * POST /lenskart/add-skills
   */
  const addSkill = useCallback(async (skillName) => {
    try {
      setAdding(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart/add-skills`,
        { name: skillName.trim() },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Adding skill failed:', error);
      throw error;
    } finally {
      setAdding(false);
    }
  }, [getAuthHeaders]);


  const updatePaymentStatus = useCallback(async ({incentiveId,status}) => {
    try {
      setAdding(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/update-incentive-payment-status`,
        { incentiveId,
          status },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Adding skill failed:', error);
      throw error;
    } finally {
      setAdding(false);
    }
  }, [getAuthHeaders]);
  /**
   * Toggle skill active/inactive status
   * PUT /lenskart/mark-skill-active-inactive/{skillId}
   */
  const skillStatusChange = useCallback(async (skill) => {
    setTogglingId(skill.id);
    try {
      const response = await axios.put(
        `${BASE_URL}/lenskart/mark-skill-active-inactive/${skill.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Skill status change failed:', error);
      throw error;
    } finally {
      setTogglingId(null);
    }
  }, [getAuthHeaders]);

  /**
   * Edit skill name
   * PUT /lenskart/update-skill/{skillId}
   */
  const editSkill = useCallback(async (editValue, skill) => {
    setSavingEdit(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/lenskart/update-skill/${skill.id}`,
        { name: editValue.trim() },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }

  }, [getAuthHeaders]);


     const list_master_department = useCallback(async (status) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/OnDemandCompanies/list-master-departments`,
        { 
          isActive:status,
        },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);
 

    const add_master_department = useCallback(async (deptname,status) => {
    setSavingEdit(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/OnDemandCompanies/add-master-department`,
        { name:deptname,
          isActive:status,
        },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);


      const edit_master_department = useCallback(async (deptname,status,id) => {
    setSavingEdit(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/OnDemandCompanies/edit-master-department/${id}`,
        { name:deptname,
          isActive:status,
        },
        { headers: getAuthHeaders() }
        );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);



       const list_master_subdepartment = useCallback(async (status) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/OnDemandCompanies/list-master-sub-department`,
        { 
          isActive:status,
        },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);
 

    const add_master_subdepartment = useCallback(async (deptname,masterdeptid,skills,status) => {
    setSavingEdit(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/OnDemandCompanies/add-master-sub-department`,
        { name:deptname,
          masterDepartmentId:masterdeptid,
          skillIds:skills,
          isActive:status,
        },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);


      const edit_master_subdepartment = useCallback(async (deptname,masterdeptid,skills,status,subdeptid) => {
    setSavingEdit(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/OnDemandCompanies/edit-master-sub-department/${subdeptid}`,
        {name:deptname,
          masterDepartmentId:masterdeptid,
          skillIds:skills,
          isActive:status,

        },
        { headers: getAuthHeaders() }
        );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);

   const fetchDepartmentsBySkills = async (companyId) => {
  const response = await axios.post(
    `${BASE_URL}/lenskart/getDepartments/Skills`,
    { companyId },
   { headers: getAuthHeaders() }
  );
  return response.data;
};

 const assignWorkers = async (payload) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/assign`,
    payload,
   { headers: getAuthHeaders() }
  );
  return response.data;
};

 const freezeDepartmentApi = async ({ companyId, records }) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/allot-department`,
    { companyId, records },
   { headers: getAuthHeaders() }
  );
  return response.data;
};

 const updateWorkerDepartmentApi = async ({ attendanceId, departmentId, prevDepartmentId }) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/update-worker-department`,
    { attendanceId, departmentId, prevDepartmentId },
   { headers: getAuthHeaders() }
  );
  return response.data;
};
const add_accessory = useCallback(async (fd) => {
  try {
    setAdding(true);

    const response = await axios.post(
      `${BASE_URL}/onDemandAccessories/add-accessory`,
      fd,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Adding Accessory failed:", error);
    throw error;
  } finally {
    setAdding(false);
  }
}, [getAuthHeaders]);

const return_accessory = useCallback(async (historyId,returnedAmount,remark) => {
  try {
    setAdding(true);

    const response = await axios.post(
      `${BASE_URL}/onDemandAccessories/return-accessory`,
      {historyId,returnedAmount,
        returnedRemarks:remark},
 { headers: getAuthHeaders() }
    );

    return response;
  } catch (error) {
    console.error("Adding Accessory failed:", error);
    throw error;
  } finally {
    setAdding(false);
  }
}, [getAuthHeaders]);


const allot_accessory = useCallback(async (companyId,workerId,accessories) => {
  try {
    setAdding(true);

    const response = await axios.post(
      `${BASE_URL}/onDemandAccessories/allot-accessory`,
      {companyId,
        workerId,
        accessoryIds:accessories
      },
      { headers: getAuthHeaders() }
    );

    return response;
  } catch (error) {
    console.error("Adding Accessory failed:", error);
    throw error;
  } finally {
    setAdding(false);
  }
}, [getAuthHeaders]);


const delete_accessory = useCallback(async (historyId) => {
  try {
    setAdding(true);

    const response = await axios.delete(
      `${BASE_URL}/onDemandAccessories/delete-accessory/${historyId}`,

      { headers: getAuthHeaders() }
    );

    return response;
  } catch (error) {
    console.error("Adding Accessory failed:", error);
    throw error;
  } finally {
    setAdding(false);
  }
}, [getAuthHeaders]);

 const edit_accessory = useCallback(async (fd, id) => {
    setSavingEdit(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/onDemandAccessories/edit-accessory/${id}`,
         fd,
             {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
      );
      return response;
    } catch (error) {
      console.error('Editing skill failed:', error);
      throw error;
    } finally {
      setSavingEdit(false);
    }
  }, [getAuthHeaders]);

  


    const fetchMonthlyInsentive = useCallback(async (workerId)=>{
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/lenskart/worker-monthly-incentive-payout/${workerId}`,
            { headers: getAuthHeaders()}
        );
        return response.data;
    } catch(error){
        console.error('Failed to fetch monthly incentives:', error);
        throw error;
    } finally {
        setLoading(false);
    }
  },[getAuthHeaders]);


  const fetchPaymentHistory = useCallback(async (params,workerId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/lenskart/payment-history/${workerId}`,
        { headers: getAuthHeaders(), params }
      );
      return response;
    } catch (error) {
      console.error('Fetching payment history failed:', error);
      throw error;
    }
  }, [getAuthHeaders]);


  // ── Campaigns ───────────────────────────────────────────────────────────────

const REFERRAL_URL = `${BASE_URL}/referral-sourcing`;

const fetchcampaigns = useCallback(async (params) => {
  try {
    return await axios.get(`${REFERRAL_URL}/campaigns`, { headers: getAuthHeaders(), params });
  } catch (error) {
    console.error('Fetching campaigns failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

const fetchCampaignById = useCallback(async (campaignCode) => {
  try {
    return await axios.get(`${REFERRAL_URL}/campaigns/${campaignCode}`, { headers: getAuthHeaders() });
  } catch (error) {
    console.error('Fetching campaign failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

const add_campaign = useCallback(async (payload) => {
  try {
    return await axios.post(`${REFERRAL_URL}/campaigns`, payload, { headers: getAuthHeaders() });
  } catch (error) {
    console.error('Adding campaign failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// campaignCode = the 4-character campaign_id (not the Mongo _id)
const edit_campaign = useCallback(async (payload, campaignCode) => {
  try {
    return await axios.patch(`${REFERRAL_URL}/campaigns/${campaignCode}`, payload, { headers: getAuthHeaders() });
  } catch (error) {
    console.error('Editing campaign failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// ── Assets ──────────────────────────────────────────────────────────────────



// 1. Create asset
// payload: { campaign_id, name, channel, source, locationId, desc?, asset_type?, suffix? }
const add_asset = useCallback(async (payload) => {
  try {
    return await axios.post(`${REFERRAL_URL}/assets`, payload, { headers: getAuthHeaders() });
  } catch (error) {
    console.error('Creating asset failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// 2. Get all assets
// params (all optional): { campaign_id, channel, status }
const fetchAssets = useCallback(async (params = {}) => {
  try {
    return await axios.get(`${REFERRAL_URL}/assets`, { params, headers: getAuthHeaders() });
  } catch (error) {
    console.error('Fetching assets failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// 3. Get asset by ID (asset code)
const fetchAssetById = useCallback(async (assetId) => {
  try {
    return await axios.get(`${REFERRAL_URL}/assets/${encodeURIComponent(assetId)}`, { headers: getAuthHeaders() });
  } catch (error) {
    console.error('Fetching asset failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// 4. Update asset
// payload (any of): { name, status, desc, asset_type, channel, source, locationId, suffix }
const edit_asset = useCallback(async (payload, assetId) => {
  try {
    return await axios.patch(`${REFERRAL_URL}/assets/${encodeURIComponent(assetId)}`, payload, { headers: getAuthHeaders() });
  } catch (error) {
    console.error('Editing asset failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// 5. Get asset options
// no channel  -> available channels
// with channel -> valid sources for that channel
const fetchAssetOptions = useCallback(async (channel) => {
  try {
    return await axios.get(`${REFERRAL_URL}/assets/options`, {
      params: channel ? { channel } : {},
      headers: getAuthHeaders(),
    });
  } catch (error) {
    console.error('Fetching asset options failed:', error);
    throw error;
  }
}, [getAuthHeaders]);

// PATCH /assets-status/:asset_id   body: { status: "active" | "inactive" }
const toggle_asset_status = useCallback(async (assetId, status) => {
  try {
    return await axios.patch(
      `${REFERRAL_URL}/assets-status/${encodeURIComponent(assetId)}`,
      { status },
      { headers: getAuthHeaders() }
    );
  } catch (error) {
    console.error('Updating asset status failed:', error);
    throw error;
  }
}, [getAuthHeaders]);


    const fetchKYWquestions = useCallback(async (workerId) => {
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/kyw/questions-with-answers/${workerId}`,
            { headers: getAuthHeaders()}
        );
        return response.data;
    } catch(error){
        console.error('Failed to fetch Questions:', error);
        throw error;
    } 
  }, [getAuthHeaders]);

      const SubmitKYWquestions = useCallback(async (payload) => {
    try{
        setLoading(true);
        const response = await axios.post(
            `${BASE_URL}/kyw/submit-form`,
            {workerId:payload.workerId,
              responses:payload.responses
            },
            { headers: getAuthHeaders()}
        );
        return response;
    } catch(error){
        console.error('Failed to fetch Questions:', error);
        throw error;
    } 
  }, [getAuthHeaders]);

  const value = {
    token,
    user,
    loading,
    initialized,
    isLoggedIn: !!token,
    isTablet,
    sendOtp,
    verifyOtp,
    logout,
    verifyToken,
    fetchLocations,
    fetchSkills,
    fetchaccessories,
    fetchCompaniesByLocations,
    updateWorkerVerificationStatus,
    fetchWorkerVerificationHistory,
    fetchOnboardingWorkers,
    searchWorker,
    downloadWorkersCSV,
    fetchLastCallStatuses,
    fetchLastCallSubStatuses,
    fetchRetentionIssues,
    downloadWorkerStepsCSV,
    fetchRegisteredWorkersCount,
    downloadRegisteredWorkersCSV,
    fetchAdminData,
    fetchOnboardingOverallReport,
    fetchSources,
    uploadCsvForSignups,
    //superadmin request history returns
    getCompanyByLocation,
    getDepartmentsByCompany,
    fetchCompaniesByLocation,
    fetchActiveCompaniesByLocation,
    fetchDepartmentTimeSlots,
    fetchFloorAdminHistory,
    updateFloorRequestStatus,
    addFloorRequirement,
    updateFloorRequirement,
    deleteFloorRequirement,

    //change payout date endpoints
    payout_change_date_shifts,
    change_payout_date,
    changepayoutmode,
    worker_payout_history,
    attendancefiledatafetch,
    // Monthly Payout
    fetchMonthlyPayout, // <-- ADDED HERE
    // Location management functions
    fetchAllLocations,
    fetchActiveLocations,
    fetchalllocation, // Added this alias
    addLocation,
    locationStatusChange,
    editLocation,
    // Skill management functions (added for completeness)
    fetchAllSkills,
    addSkill,
    skillStatusChange,
    editSkill,
    // Loading states
    adding,
    togglingId,
    savingEdit,
    add_master_department,
    edit_master_department,
    list_master_department,
     add_master_subdepartment,
    edit_master_subdepartment,
    list_master_subdepartment,
    fetchDepartmentsBySkills,
    assignWorkers,
    freezeDepartmentApi,
    updateWorkerDepartmentApi,
    fetchMonthlyInsentive,
    fetchPaymentHistory,
    add_accessory,
    edit_accessory,
    fetchaccessoriesbycompany,
    allot_accessory,
    fetchaccessorieshistory,
    return_accessory,
    delete_accessory,
    updatePaymentStatus,
    fetchKYWquestions,
    SubmitKYWquestions,
    fetchcampaigns,
    add_campaign,
    edit_campaign,
    add_asset,
    edit_asset,
    fetchAssets,
    fetchAssetById,
    fetchAssetOptions,
    toggle_asset_status
    

  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const ctx = useContext(SuperAdminContext);
  if (!ctx) throw new Error("useSuperAdmin must be used inside <SuperAdminProvider>");
  return ctx;
};